/**
 * diagnosticsService — remote error log + local diagnostics for the card-reader path (Jose 2026-09-22).
 *
 * WHY: with 5 M2 readers across 5 stations on barbers' own phones, when something breaks Jose needs to
 * text "Station 2 is acting up" and have me see the EXACT error live, then fix + deploy. This writes
 * every reader/payment event (fail OR success) to Firestore `terminalDiagnostics` with device / station /
 * operator / reader / version / env context (queryable with admin access), and keeps an in-memory ring
 * of recent events so the in-app Diagnostics screen works even offline. Best-effort — logging NEVER
 * throws into the payment path.
 * BEHAVIOR-PIN: project-slik-backup-register-capacitor-staff-2026-09-22 · reader diagnostics.
 */
import { db } from '../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { pinLockService } from './pinLockService';

const DEVICE_ID_KEY = 'slik_device_id';
const STATION_LABEL_KEY = 'slik_station_label'; // human label for THIS device, e.g. "Front chair"
const COLLECTION = 'terminalDiagnostics';

export type DiagLevel = 'error' | 'warn' | 'info';
export interface DiagEvent {
  level: DiagLevel;
  event: string;      // e.g. 'connect_failed', 'charge_declined', 'reader_not_found', 'charge_success'
  message?: string;   // error text / detail
  readerSerial?: string;
  amount?: number;    // dollars, when relevant
  // Stripe decline detail (Jose 2026-10-30) — populated on charge_failed so the real reason is captured:
  code?: string;        // Stripe error code, e.g. "card_declined"
  declineCode?: string; // Stripe decline_code, e.g. "do_not_honor" | "card_not_supported" | "insufficient_funds"
  cardBrand?: string;   // "amex" | "visa" | "mastercard" | ...
  last4?: string;       // last 4 of the attempted card, when known
  tsClient: number;   // client epoch ms (device clock)
}

// Persistent per-device id so I can tie a stream of events to one physical phone/iPad.
export function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const id: string = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
      ? (crypto as any).randomUUID()
      : `dev_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e9).toString(36)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch { return 'dev_unknown'; }
}

export function getStationLabel(): string { try { return localStorage.getItem(STATION_LABEL_KEY) || ''; } catch { return ''; } }
export function setStationLabel(label: string): void { try { if (label) localStorage.setItem(STATION_LABEL_KEY, label); else localStorage.removeItem(STATION_LABEL_KEY); } catch { /* */ } }

// Running build hash (main.<hash>.js) — same signal versionGuard uses; our "app version" for support.
export function appVersion(): string {
  try {
    for (const s of Array.from(document.getElementsByTagName('script'))) {
      const m = (s.getAttribute('src') || '').match(/main\.[a-z0-9]+\.js/);
      if (m) return m[0];
    }
  } catch { /* */ }
  return 'unknown';
}

export function env(): 'prod' | 'staging' | 'dev' {
  const pid = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'slik-pos-barberlegacy';
  if (pid === 'slik-pos-barberlegacy') return 'prod';
  if (pid.includes('staging')) return 'staging';
  return 'dev';
}

export function platform(): string {
  try {
    if ((window as any).Capacitor?.isNativePlatform?.()) return 'capacitor';
    if ((window as any).webkit?.messageHandlers?.slikTerminal) return 'ios-webview';
  } catch { /* */ }
  return 'web';
}

// In-memory ring of the most recent events on THIS device — powers the Diagnostics screen offline.
const RING_MAX = 30;
const ring: DiagEvent[] = [];
export function getRecentEvents(): DiagEvent[] { return ring.slice().reverse(); } // newest first

/** Log a reader/payment event. Best-effort: writes to Firestore + the local ring; never throws. */
export function logTerminalEvent(e: Omit<DiagEvent, 'tsClient'>): void {
  const evt: DiagEvent = { ...e, tsClient: Date.now() };
  try {
    ring.push(evt);
    if (ring.length > RING_MAX) ring.shift();
  } catch { /* */ }
  try {
    const op = pinLockService.getCurrentBarber();
    // fire-and-forget; a logging failure must never affect the checkout
    void addDoc(collection(db, COLLECTION), {
      ts: serverTimestamp(),
      tsClient: evt.tsClient,
      level: evt.level,
      event: evt.event,
      message: evt.message || '',
      readerSerial: evt.readerSerial || '',
      amount: typeof evt.amount === 'number' ? evt.amount : null,
      code: evt.code || null,
      declineCode: evt.declineCode || null,
      cardBrand: evt.cardBrand || null,
      last4: evt.last4 || null,
      deviceId: getDeviceId(),
      stationLabel: getStationLabel(),
      barberId: op?.barberId || '',
      barberName: op?.barberName || '',
      appVersion: appVersion(),
      env: env(),
      platform: platform(),
      userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
    }).catch(() => { /* offline / rules / transient — the local ring still has it */ });
  } catch { /* db not ready — ignore */ }
}

/** A copy-paste-able snapshot for the Diagnostics screen's "Copy" button. */
export function diagnosticsSnapshot(extra?: Record<string, string>): string {
  const lines = [
    `SLIK diagnostics — ${new Date().toISOString()}`,
    `env: ${env()}   platform: ${platform()}`,
    `appVersion: ${appVersion()}`,
    `deviceId: ${getDeviceId()}`,
    `station: ${getStationLabel() || '(unlabeled)'}`,
    ...(extra ? Object.entries(extra).map(([k, v]) => `${k}: ${v}`) : []),
    '',
    'recent events (newest first):',
    ...getRecentEvents().map((r) => `  ${new Date(r.tsClient).toLocaleTimeString()}  [${r.level}] ${r.event}${r.message ? ' — ' + r.message : ''}`),
  ];
  return lines.join('\n');
}
